import React, { useMemo } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

type ThreatGeoPoint = {
  countryCode: string;
  count: number;
};

type MapPoint = {
  x: number;
  y: number;
  label: string;
};

const COUNTRY_POSITIONS: Record<string, MapPoint> = {
  US: { x: 200, y: 170, label: 'United States' },
  CA: { x: 190, y: 120, label: 'Canada' },
  MX: { x: 180, y: 225, label: 'Mexico' },
  BR: { x: 305, y: 330, label: 'Brazil' },
  AR: { x: 290, y: 410, label: 'Argentina' },
  CL: { x: 255, y: 390, label: 'Chile' },
  GB: { x: 470, y: 118, label: 'United Kingdom' },
  FR: { x: 490, y: 145, label: 'France' },
  DE: { x: 515, y: 130, label: 'Germany' },
  NL: { x: 500, y: 123, label: 'Netherlands' },
  ES: { x: 468, y: 175, label: 'Spain' },
  IT: { x: 525, y: 168, label: 'Italy' },
  PL: { x: 540, y: 122, label: 'Poland' },
  UA: { x: 585, y: 132, label: 'Ukraine' },
  TR: { x: 620, y: 182, label: 'Turkey' },
  RU: { x: 690, y: 82, label: 'Russia' },
  EG: { x: 570, y: 238, label: 'Egypt' },
  ZA: { x: 565, y: 405, label: 'South Africa' },
  NG: { x: 515, y: 278, label: 'Nigeria' },
  SA: { x: 635, y: 236, label: 'Saudi Arabia' },
  IN: { x: 710, y: 235, label: 'India' },
  PK: { x: 680, y: 215, label: 'Pakistan' },
  CN: { x: 810, y: 185, label: 'China' },
  HK: { x: 835, y: 215, label: 'Hong Kong' },
  TW: { x: 855, y: 210, label: 'Taiwan' },
  JP: { x: 905, y: 183, label: 'Japan' },
  KR: { x: 875, y: 178, label: 'South Korea' },
  SG: { x: 790, y: 292, label: 'Singapore' },
  ID: { x: 825, y: 320, label: 'Indonesia' },
  TH: { x: 775, y: 268, label: 'Thailand' },
  VN: { x: 800, y: 250, label: 'Vietnam' },
  PH: { x: 845, y: 258, label: 'Philippines' },
  AU: { x: 865, y: 390, label: 'Australia' },
  NZ: { x: 955, y: 430, label: 'New Zealand' },
};

interface IronDomeGeoMapProps {
  points: ThreatGeoPoint[];
  totalThreats: number;
  knownThreats: number;
}

export default function IronDomeGeoMap({ points, totalThreats, knownThreats }: IronDomeGeoMapProps) {
  const plottedPoints = useMemo(() => {
    const maxCount = Math.max(...points.map((point) => point.count), 1);

    return points
      .map((point) => {
        const position = COUNTRY_POSITIONS[point.countryCode];
        if (!position) {
          return null;
        }

        return {
          ...point,
          ...position,
          radius: 6 + (point.count / maxCount) * 18,
        };
      })
      .filter((point): point is NonNullable<typeof point> => Boolean(point))
      .sort((left, right) => right.count - left.count);
  }, [points]);

  const unresolvedThreats = Math.max(totalThreats - knownThreats, 0);
  const coverage = totalThreats > 0 ? Math.round((knownThreats / totalThreats) * 100) : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: 420,
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        background:
          'radial-gradient(circle at 20% 20%, rgba(66,165,245,0.25), transparent 30%), radial-gradient(circle at 80% 0%, rgba(239,83,80,0.18), transparent 28%), linear-gradient(180deg, #08121f 0%, #091827 45%, #07111b 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'absolute', top: 16, left: 20, right: 20, zIndex: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h6" sx={{ color: 'common.white', fontWeight: 700 }}>
              Attacker Geolocation Theater
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
              Recent threats projected by resolved country intelligence.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`Geo coverage ${coverage}%`} color={coverage >= 70 ? 'success' : coverage >= 40 ? 'warning' : 'default'} />
            <Chip label={`${knownThreats} resolved`} color="info" variant="outlined" />
            <Chip label={`${unresolvedThreats} unresolved`} color={unresolvedThreats > 0 ? 'warning' : 'success'} variant="outlined" />
          </Stack>
        </Stack>
      </Box>

      <svg viewBox="0 0 1000 500" style={{ width: '100%', height: '100%', display: 'block' }}>
        <g opacity="0.15" fill="none" stroke="rgba(255,255,255,0.5)">
          <path d="M80 115 C140 90, 210 88, 260 122 C300 148, 320 185, 305 220 C280 250, 220 270, 175 262 C118 252, 72 212, 58 175 C55 150, 60 130, 80 115 Z" />
          <path d="M270 270 C310 282, 330 312, 333 352 C330 394, 320 425, 296 452 C278 440, 268 420, 258 390 C248 356, 244 320, 270 270 Z" />
          <path d="M438 108 C496 88, 570 90, 628 112 C678 131, 725 148, 792 154 C852 164, 915 182, 940 205 C920 232, 872 236, 842 252 C814 268, 792 294, 767 309 C736 330, 700 330, 662 318 C635 300, 606 281, 578 285 C540 293, 504 274, 478 248 C446 218, 428 182, 438 108 Z" />
          <path d="M485 230 C512 220, 542 230, 562 252 C582 279, 588 309, 580 342 C564 374, 548 412, 516 432 C490 410, 478 380, 472 344 C470 304, 468 268, 485 230 Z" />
          <path d="M805 348 C840 334, 888 338, 926 356 C946 374, 944 403, 916 420 C884 434, 846 437, 816 423 C794 404, 790 376, 805 348 Z" />
        </g>

        <g fill="rgba(255,255,255,0.28)" fontSize="14" fontFamily="system-ui, sans-serif">
          <text x="140" y="80">NORTH AMERICA</text>
          <text x="266" y="472">SOUTH AMERICA</text>
          <text x="515" y="92">EUROPE / ASIA</text>
          <text x="500" y="470">AFRICA</text>
          <text x="835" y="470">OCEANIA</text>
        </g>

        {plottedPoints.map((point) => (
          <g key={point.countryCode}>
            <circle cx={point.x} cy={point.y} r={point.radius + 10} fill="rgba(239, 83, 80, 0.10)" />
            <circle cx={point.x} cy={point.y} r={point.radius} fill="rgba(239, 83, 80, 0.85)" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
            <text
              x={point.x}
              y={point.y - point.radius - 10}
              textAnchor="middle"
              fill="rgba(255,255,255,0.92)"
              fontSize="12"
              fontFamily="system-ui, sans-serif"
            >
              {point.countryCode}
            </text>
          </g>
        ))}
      </svg>

      <Box sx={{ position: 'absolute', bottom: 16, left: 20, right: 20 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          {plottedPoints.slice(0, 8).map((point) => (
            <Tooltip key={point.countryCode} title={`${point.label}: ${point.count} threat${point.count === 1 ? '' : 's'}`}>
              <Chip
                label={`${point.countryCode} ${point.count}`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'common.white',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              />
            </Tooltip>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}